import { Paths } from "@app/routes";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
  Label,
  WithToolTip,
} from "@atlas/core";
import { formatIsoDateTimeDisplay, useTranslation } from "@atlas/sdk";
import type { IHubPackageVisibility } from "@enums";
import type { IHubPackage } from "@models";
import {
  EditablePackageVisibility,
  PackageActionsBar,
  PackageSwitcher,
} from "@shared/components";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

interface IPackageSummaryCardProps {
  isPopover: boolean;
  /** Primary label (e.g. solution catalog or package label). */
  titleLabel: string;
  /** Number of line items in the cart. */
  itemCount: number;
  /** Oldest package `createdByDatetime` ISO string, if any. */
  createdAtIso: string | null;
  submitDisabled: boolean;
  cartPackage?: IHubPackage | null;
  onCancel?: () => void;
  onEdit?: () => void;
  onCreate?: () => void;
  visibility?: IHubPackageVisibility;
  formId?: string;
}

export function PackageSummaryCard({
  isPopover,
  titleLabel,
  itemCount,
  createdAtIso,
  submitDisabled,
  cartPackage,
  onCancel,
  onEdit,
  onCreate,
  visibility,
  formId,
}: IPackageSummaryCardProps) {
  const { t } = useTranslation();
  const [isSwitchingPackage, setIsSwitchingPackage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const createdLabel = useMemo(() => {
    if (createdAtIso == null || createdAtIso === "") return null;
    const d = new Date(createdAtIso);
    if (Number.isNaN(d.getTime())) return null;
    const formatted = formatIsoDateTimeDisplay(createdAtIso);
    return t("hub.checkout.summary_created", {
      date: formatted,
      defaultValue: "Created on {{date}}",
    });
  }, [createdAtIso, t]);

  const itemsLabel = useMemo(
    () =>
      itemCount === 1
        ? t("hub.checkout.summary_cart_item", {
            count: itemCount,
            defaultValue: "{{count}} item in package",
          })
        : t("hub.checkout.summary_cart_items", {
            count: itemCount,
            defaultValue: "{{count}} items in package",
          }),
    [itemCount, t],
  );

  return (
    <Card className={cn("min-w-0", isPopover && "border-0 shadow-none")}>
      {!isPopover && (
        <CardHeader className="min-w-0">
          <CardTitle className="flex w-full min-w-0 items-center justify-between gap-2 border-0">
            {isSwitchingPackage ? (
              <PackageSwitcher
                currentPackageId={cartPackage?.packageId}
                onCancel={() => setIsSwitchingPackage(false)}
                onSuccess={(pkg) => {
                  setIsSwitchingPackage(false);
                  if (searchParams.has("packageId") && pkg.packageId != null) {
                    setSearchParams((prev) => {
                      prev.set("packageId", pkg.packageId?.toString() ?? "");
                      prev.set("fromTab", "packages");
                      return prev;
                    });
                  }
                }}
              />
            ) : (
              <>
                <div className="flex items-center gap-2 overflow-hidden h-full">
                  <WithToolTip tooltipText={titleLabel}>
                    <Label className="truncate text-lg">{titleLabel}</Label>
                  </WithToolTip>
                </div>
                <div className="flex items-center">
                  <PackageActionsBar
                    cartPackage={cartPackage}
                    itemCount={itemCount}
                    onEdit={onEdit}
                    onCreate={onCreate}
                    onSwitch={() => setIsSwitchingPackage(true)}
                    buttonClassName="size-8 shrink-0"
                  />
                </div>
              </>
            )}
          </CardTitle>
        </CardHeader>
      )}
      {!isPopover && (
        <CardContent className="flex flex-col gap-2">
          <EditablePackageVisibility
            pkg={cartPackage}
            fallbackVisibility={visibility}
          />
          <CardDescription>{itemsLabel}</CardDescription>
          {createdLabel && <CardDescription>{createdLabel}</CardDescription>}
        </CardContent>
      )}

      <CardFooter className={cn("border-0", isPopover && "p-0")}>
        <div
          className={cn(
            "flex w-full items-center",
            isPopover ? "justify-end gap-2" : "justify-end gap-4",
          )}>
          {isPopover && onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("hub.administer.cancel", { defaultValue: "Cancel" })}
            </Button>
          )}
          {!isPopover && (
            <Button
              type="button"
              variant={submitDisabled ? "default" : "outline"}
              asChild>
              <Link to={`/${Paths.CatalogManager}?tab=explorer`}>
                {t("hub.checkout.browse_explorer", {
                  defaultValue: "Browse catalog",
                })}
              </Link>
            </Button>
          )}
          {!submitDisabled && (
            <Button
              type="submit"
              form={formId}
              className={cn(isPopover ? "w-25" : "w-full sm:w-auto")}
              disabled={submitDisabled}>
              {isPopover ? (
                <>
                  {t("hub.checkout.install", { defaultValue: "Install" })}
                  <ArrowRight className=" size-4" />
                </>
              ) : (
                t("hub.checkout.continue_subscriptions", {
                  defaultValue: "Continue to install",
                })
              )}
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
